import { NgClass, NgIf } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  Renderer2
} from '@angular/core';
import {
  AuraIcon,
  AuraIconComponent,
  LinkDirective,
  RibbonDirective
} from '@com-delta-omnichannel/aura';
import { scrollToTop } from '@com-delta-omnichannel/aura-utils';
import { getNotificationIcon } from './adapters';
import { Notification } from './models/notification.interfaces';
import * as Utils from './utils';

@Component({
  selector: 'idp-notification',
  standalone: true,
  imports: [NgIf, NgClass, AuraIconComponent, RibbonDirective, LinkDirective],
  templateUrl: './notification.component.html',
  styleUrls: ['./notification.component.scss']
})
export class NotificationComponent implements AfterViewInit, OnDestroy {
  @Input() target = '';
  @Input() dismissAfter = 8000;

  notification: Notification | null = null;
  icon!: AuraIcon;

  protected readonly scrollTo = Utils.scrollTo;

  private notificationElement!: HTMLElement | null;
  private observer: IntersectionObserver | null = null;
  private isFirstRender = true;
  private hideTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private elementRef: ElementRef<HTMLDivElement>,
    private renderer: Renderer2,
    private element: ElementRef
  ) {}

  @Input() set content(value: Notification) {
    this.init(value);
  }

  ngAfterViewInit() {
    if (this.notification) {
      this.init(this.notification);
    }
  }

  init(notification: Notification) {
    this.notification = notification;
    this.icon = getNotificationIcon(notification.type);
    if (this._skipFirstRender()) {
      return;
    }

    const targetElement = Utils.findElement(this.target);

    if (this.target && targetElement) {
      this.notificationElement =
        this.elementRef.nativeElement.querySelector('.notification');

      this.observer = Utils.initObserver(
        targetElement,
        this.notificationElement,
        this._onIntersect.bind(this),
        this._toggleSticky.bind(this)
      );
    } else {
      scrollToTop();
    }

    Utils.startDismissTimer(
      notification.type,
      this.dismissAfter,
      this.hideTimeout,
      (timeout) => (this.hideTimeout = timeout),
      () => (this.notification = null)
    );
    this.validateFocus(notification.focusRequired);
  }

  validateFocus(focusRequired: boolean | undefined) {
    if (focusRequired !== false) this.focusNotification();
  }

  focusNotification() {
    setTimeout(() => {
      this.element.nativeElement.querySelector('#notification-focus')?.focus();
    }, 0);
  }

  ngOnDestroy() {
    this.isFirstRender = true;
    Utils.cleanupObserver(this.observer);
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
    }
  }

  private _skipFirstRender(): boolean {
    if (this.isFirstRender && !this.target) {
      this.isFirstRender = false;
      return true;
    }
    return false;
  }

  private _onIntersect(entries: IntersectionObserverEntry[]) {
    const [entry] = entries;
    this._toggleSticky(!entry.isIntersecting);
  }

  private _toggleSticky(sticky: boolean) {
    Utils.toggleStickyClass(this.notificationElement, sticky, this.renderer);
  }
}
